/**
 * What each installed backdrop actually depicts, so a location can be matched
 * to a plate instead of drawing one at random.
 *
 * The keys are slugs — the filename without its extension — of the files in
 * `backgrounds/rest`, `backgrounds/fight` and `backgrounds/duel`. The tags come
 * from the per-bank `MANIFEST.md` under `Art/generated/{long-rest,fight-scenes,
 * duel-invite}/`, which records what each plate was rendered to be.
 *
 * A plate carries every tag that is true of it: a lighthouse room is both an
 * `interior` and `coastal`, and a position labelled with either should be able
 * to reach it.
 *
 * `src/tests/component/backgroundManifest.spec.js` asserts a bijection between
 * these entries and the files actually installed, so a bank that is regenerated
 * without updating this map fails the gate rather than silently losing plates.
 */

export type EnvTag =
  | 'underground'
  | 'mirror'
  | 'interior'
  | 'urban'
  | 'ruin'
  | 'wilderness'
  | 'coastal'
  | 'arctic'
  | 'desert';

export const ENV_TAGS: readonly EnvTag[] = [
  'underground',
  'mirror',
  'interior',
  'urban',
  'ruin',
  'wilderness',
  'coastal',
  'arctic',
  'desert',
];

export const REST_TAGS: Record<string, EnvTag[]> = {
  '01-campfire-in-ruins': ['ruin'],
  '03-safehouse-interior-with-warm-lamplight': ['interior', 'urban'],
  '04-quiet-medical-bay-bunk': ['interior'],
  '07-cave-with-glowing-mineral-light': ['underground'],
  '19-subterranean-vault-chamber': ['underground'],
  '20-watchtower-bunk-at-dawn': ['interior'],
  'ug-undercroft-bedroll': ['underground'],
  'ug-flooded-passage-camp': ['underground'],
  'ug-bazaar-niche': ['underground'],
  'ug-junction-alcove': ['underground'],
  'ug-mineral-seam-chamber': ['underground'],
  'ug-barricaded-storeroom': ['underground'],
  'ug-dry-ledge-water': ['underground'],
  'mirror-quiet-bedroll': ['mirror'],
  'mirror-lamp-repetition': ['mirror'],
  'mirror-still-water-room': ['mirror'],
  'pub-closed-booth': ['interior'],
  'pub-back-room-cots': ['interior'],
  'urban-plinth-camp': ['urban'],
  'urban-sheltered-doorway': ['urban'],
};

export const FIGHT_TAGS: Record<string, EnvTag[]> = {
  '01-rain-plaza': ['urban'],
  '02-scrapyard': ['urban', 'ruin'],
  '03-cathedral-ruin': ['ruin'],
  '04-market-square': ['urban'],
  '05-cargo-bay': ['interior'],
  '06-chasm-bridge': ['wilderness'],
  '07-neon-alley': ['urban'],
  '08-frozen-lake': ['arctic'],
  '09-desert-pillars': ['desert', 'ruin'],
  '10-rooftop-helipad': ['urban'],
  '11-flooded-street': ['urban'],
  '12-fighting-pit': ['interior', 'urban'],
  '13-standing-stones': ['wilderness', 'ruin'],
  '14-train-platform': ['interior', 'urban'],
  '15-foundry-floor': ['interior'],
  '16-hangar': ['interior'],
  '17-compound-courtyard': ['urban'],
  '18-low-tide-beach': ['coastal'],
  '19-canyon-floor': ['wilderness', 'desert'],
  '20-broken-greenhouse': ['interior', 'ruin'],
  '21-tunnel-junction': ['underground'],
  '22-quarry-terrace': ['wilderness'],
  '23-dance-hall': ['interior', 'urban'],
  '24-rope-bridge-village': ['wilderness'],
  '25-power-substation': ['urban'],
  '26-mine-landing': ['underground'],
  '27-sunken-amphitheatre': ['ruin'],
  '28-cliff-edge': ['wilderness', 'coastal'],
  '29-stripped-garage': ['interior', 'urban'],
  '30-checkpoint-gate': ['urban'],
};

export const DUEL_TAGS: Record<string, EnvTag[]> = {
  '01-dawn-courtyard': ['urban'],
  '02-rain-rooftop': ['urban'],
  '03-torchlit-hall': ['interior'],
  '04-sand-arena': ['ruin'],
  '05-frozen-lake': ['arctic'],
  '06-moonlit-garden': ['urban'],
  '07-derelict-chapel': ['ruin'],
  '08-canyon-ledge': ['wilderness'],
  '09-rooftop-garden': ['urban'],
  '10-cellar-vault': ['underground', 'interior'],
  '11-boardwalk-dusk': ['coastal'],
  '12-snow-bridge': ['arctic'],
  '13-ruined-amphitheatre': ['ruin'],
  '14-greenhouse-aisle': ['interior'],
  '15-lantern-dock': ['coastal'],
  '16-star-plateau': ['wilderness'],
  '17-undercroft-vault': ['underground'],
  '18-flooded-gallery': ['underground'],
  '19-underground-market-aisle': ['underground'],
  '20-tunnel-junction': ['underground'],
  '21-mirror-chamber': ['mirror'],
  '22-mirror-impossible-room': ['mirror'],
  '23-taproom-cleared': ['interior'],
  '24-monument-plaza-night': ['urban'],
};
